// common/features/competition/wizard/validation.ts
import * as Yup from 'yup'
import dayjs from 'dayjs'
import { competitionWord } from './utils'

// Compare dates at calendar-day granularity, normalized to the user's local
// day. Preloaded clone sources carry a UTC timestamp (e.g.
// "2026-06-23T00:00:00+00:00") in startsAt, while the date pickers display and
// emit plain "YYYY-MM-DD" in local time. Slicing the raw string would read the
// UTC day, which can be one ahead of the day the user actually sees/picks.
// dayjs().format() resolves both to the local calendar day, matching the
// picker, so a same-day end date passes.
const toDayKey = (value: string): string => dayjs(value).format('YYYY-MM-DD')

const isEndOnOrAfterStart = (
  startsAt: string | null | undefined,
  endsAt: string | null | undefined
): boolean => {
  if (!startsAt || !endsAt) {
    return true
  }
  return toDayKey(endsAt) >= toDayKey(startsAt)
}

export const step1Schema = Yup.object({
  creationMethod: Yup.mixed<'new' | 'clone'>()
    .oneOf(['new', 'clone'])
    .required('Pick a creation method'),
})

export const step1CloneSchema = Yup.object({
  sourceScheduleId: Yup.string()
    .nullable()
    .required(
      () => `Select a ${competitionWord({ capitalize: false })} to clone`
    ),
  name: Yup.string().trim().required('Name is required'),
  startsAt: Yup.string().nullable().required('Start date is required'),
  endsAt: Yup.string()
    .nullable()
    .required('End date is required')
    .test(
      'after-start',
      'End date must be on or after start date',
      function (value) {
        return isEndOnOrAfterStart(this.parent.startsAt, value)
      }
    ),
  competitionType: Yup.mixed<
    'season' | 'playoffs_simple' | 'playoffs_enhanced' | 'tournament'
  >()
    .oneOf(['season', 'playoffs_simple', 'playoffs_enhanced', 'tournament'])
    .required(() => `Pick a ${competitionWord({ capitalize: false })} type`),
  isTournament: Yup.boolean().required(),
  isRegistrationSchedule: Yup.boolean().required(),
  cloneRegistrationSettings: Yup.boolean().required(),
  cloneTeams: Yup.mixed<'none' | 'empty' | 'full'>()
    .oneOf(['none', 'empty', 'full'])
    .required(),
})

// Lightweight URL check — accepts plain hostnames (e.g. "competition.com")
// in addition to fully-qualified URLs. Yup's built-in .url() rejects
// hostname-only input, which is too strict for what the form accepts.
const isLikelyUrl = (v: string) =>
  /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/.*)?$/i.test(v.trim())

export const step2Schema = Yup.object({
  sportId: Yup.string().nullable().required('Sport is required'),
  name: Yup.string()
    .trim()
    .required(() => `${competitionWord()} name is required`),
  startsAt: Yup.string().nullable().required('Start date is required'),
  endsAt: Yup.string()
    .nullable()
    .required('End date is required')
    .test(
      'after-start',
      'End date must be on or after start date',
      function (value) {
        return isEndOnOrAfterStart(this.parent.startsAt, value)
      }
    ),
  // Format-only when non-empty. The cross-field "at least one of" rule is
  // attached to `website` below so a single error message surfaces when both
  // are empty.
  email: Yup.string()
    .trim()
    .test('email-format', 'Enter a valid email address', (value) => {
      if (!value) {
        return true
      }
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    }),
  website: Yup.string()
    .trim()
    .test('url-format', 'Enter a valid website URL', (value) => {
      if (!value) {
        return true
      }
      return isLikelyUrl(value)
    })
    .test('email-or-website', 'Enter an email or website', function (value) {
      const email = (this.parent.email || '').trim()
      const website = (value || '').trim()
      return Boolean(email) || Boolean(website)
    }),
})

export const step3Schema = Yup.object({
  competitionType: Yup.mixed<
    'season' | 'playoffs_simple' | 'playoffs_enhanced' | 'tournament'
  >()
    .oneOf(['season', 'playoffs_simple', 'playoffs_enhanced', 'tournament'])
    .required(() => `Pick a ${competitionWord({ capitalize: false })} format`),
  // Enhanced Playoffs is the only format that requires a clone source on
  // Step 3. Simple Playoffs and Tournament accept an optional source (with
  // it they short-circuit to /clone; without it they walk the full flow).
  cloneSource: Yup.object().when('competitionType', {
    is: (v: unknown) => v === 'playoffs_enhanced',
    then: () =>
      Yup.object({
        sourceScheduleId: Yup.string()
          .nullable()
          .required(
            () =>
              `Select a ${competitionWord({
                capitalize: false,
              })} to clone from`
          ),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
})

export const step4Schema = Yup.object({
  structureType: Yup.mixed<'single' | 'multiple' | 'conferences'>()
    .oneOf(['single', 'multiple', 'conferences'])
    .required('Pick a structure'),
})

const divisionDraftShape = Yup.object({
  clientId: Yup.string().required(),
  name: Yup.string().trim().required('Division name is required'),
  order: Yup.number().required(),
})

export const step4DivisionsSchema = Yup.object({
  divisions: Yup.array()
    .of(divisionDraftShape)
    .min(1, 'Add at least one division')
    .required(),
})

const conferenceDraftShape = Yup.object({
  clientId: Yup.string().required(),
  name: Yup.string().trim().required('Conference name is required'),
  order: Yup.number().required(),
  divisions: Yup.array().of(divisionDraftShape).required(),
  isExpanded: Yup.boolean().required(),
})

export const step4ConferencesSchema = Yup.object({
  conferences: Yup.array()
    .of(conferenceDraftShape)
    .min(1, 'Add at least one conference')
    .required(),
})

export const step5Schema = Yup.object({
  joinType: Yup.mixed<'registration' | 'manual'>()
    .oneOf(['registration', 'manual'])
    .required('Pick how teams will join'),
})

// Yup.when() invokes the predicate with the raw field values in the order
// they're listed in the dependency array.
//
// True when the Structure step values are still required (i.e. the wizard
// isn't going to short-circuit to /clone on Summary submit).
const requiresStructureStep = (
  creationMethod: unknown,
  competitionType: unknown,
  cloneSource: unknown
): boolean => {
  if (creationMethod !== 'new') {
    return false
  }
  if (competitionType === 'playoffs_enhanced') {
    return false
  }
  const hasSource = Boolean(
    (cloneSource as { sourceScheduleId?: string | null } | null)
      ?.sourceScheduleId
  )
  if (
    (competitionType === 'playoffs_simple' ||
      competitionType === 'tournament') &&
    hasSource
  ) {
    return false
  }
  return true
}

// True when the Join step is required. Same as Structure except Simple
// Playoffs without a source also skips Join (it's a manual bracket by
// definition — the payload builder forces registration_type: 'manual').
const requiresJoinStep = (
  creationMethod: unknown,
  competitionType: unknown,
  cloneSource: unknown
): boolean => {
  if (!requiresStructureStep(creationMethod, competitionType, cloneSource)) {
    return false
  }
  return competitionType !== 'playoffs_simple'
}

export const wizardSchema = Yup.object({
  creationMethod: Yup.mixed<'new' | 'clone'>()
    .oneOf(['new', 'clone'])
    .required(),

  cloneSource: Yup.object().when('creationMethod', {
    is: 'clone',
    then: () => step1CloneSchema,
    otherwise: (schema) => schema.notRequired(),
  }),

  info: Yup.object().when('creationMethod', {
    is: 'new',
    then: () => step2Schema,
    otherwise: (schema) => schema.notRequired(),
  }),

  competitionType: Yup.mixed().when('creationMethod', {
    is: 'new',
    then: () =>
      Yup.mixed<
        'season' | 'playoffs_simple' | 'playoffs_enhanced' | 'tournament'
      >()
        .oneOf(['season', 'playoffs_simple', 'playoffs_enhanced', 'tournament'])
        .required(),
    otherwise: (schema) => schema.notRequired(),
  }),

  // Structure/Divisions/Conferences/Join only apply to new-flow paths that
  // don't short-circuit to /clone. Enhanced Playoffs always shorts; Simple
  // Playoffs and Tournament short only when a clone source was picked.
  structureType: Yup.mixed().when(
    ['creationMethod', 'competitionType', 'cloneSource'],
    {
      is: requiresStructureStep,
      then: () =>
        Yup.mixed<'single' | 'multiple' | 'conferences'>()
          .oneOf(['single', 'multiple', 'conferences'])
          .required(),
      otherwise: (schema) => schema.notRequired(),
    }
  ),

  // 'single' allows empty (whole competition is the one division);
  // 'multiple' requires at least one named division.
  divisions: Yup.array().when('structureType', {
    is: 'multiple',
    then: () =>
      Yup.array()
        .of(divisionDraftShape)
        .min(1, 'Add at least one division')
        .required(),
    otherwise: (schema) => schema.notRequired(),
  }),

  conferences: Yup.array().when('structureType', {
    is: 'conferences',
    then: () =>
      Yup.array()
        .of(conferenceDraftShape)
        .min(1, 'Add at least one conference')
        .required(),
    otherwise: (schema) => schema.notRequired(),
  }),

  joinType: Yup.mixed().when(
    ['creationMethod', 'competitionType', 'cloneSource'],
    {
      is: requiresJoinStep,
      then: () =>
        Yup.mixed<'registration' | 'manual'>()
          .oneOf(['registration', 'manual'])
          .required(),
      otherwise: (schema) => schema.notRequired(),
    }
  ),
})
